<template>
  <div v-if="flat" class="flat__wrapper" :class="{ active: sidebarActive }">
    <div class="flat__container">
      <div class="flat">
        <div class="flat__imageWrapper">
          <transition name="fade" mode="out-in">
            <div
              class="flat__image"
              v-if="currentView == '2D'"
              :data-zoomable="true"
            >
              <zoomImage
                v-if="planURL"
                :src="planURL"
                key="2d"
                @error="
                  () => {
                    planError = true;
                  }
                "
              />
              <p v-else class="flat__noimage">{{ $t('singleFlat.noPrev') }}</p>
            </div>
            <div class="flat__image" v-else key="3d" :data-zoomable="true">
              <zoomImage
                v-if="threeURL"
                :src="threeURL"
                @error="
                  e => {
                    e.target.style.visibility = 'hidden';
                    changeView('2D');
                    threeDError = true;
                  }
                "
              />
              <p v-else class="flat__noimage">{{ $t('singleFlat.noPrev') }}</p>
            </div>
          </transition>

          <div class="flat__viewButtons" v-if="!threeDError">
            <button
              class="flat__viewButton"
              @click="
                () => {
                  changeView('3D');
                }
              "
              :class="{ active: currentView == '3D' }"
            >
              3D
            </button>
            <button
              class="flat__viewButton"
              @click="
                () => {
                  changeView('2D');
                }
              "
              :class="{ active: currentView == '2D' }"
            >
              2D
            </button>
          </div>

          <div class="flat__similar">
            <p class="flat__heading">{{ $t('singleFlat.similar') }}</p>
            <div class="flat__cards" v-if="similars">
              <card
                v-for="similar in similars"
                :key="similar[flatProperties['id']]"
                :flat="similar"
              ></card>
            </div>
          </div>
        </div>

        <div class="flat__info">
          <transition name="fade">
            <flat-form
              :flat="flat[flatProperties['id']]"
              v-if="formActive"
              @close="toggleForm"
            ></flat-form>
          </transition>

          <button class="flat__favourite" @click="toggleFavourite">
            <img
              src="@/assets/heart.svg"
              alt="heart"
              v-if="isFavourite"
              title="Usuń z ulubionych"
            />
            <img
              src="@/assets/heart-empty.svg"
              alt="heart"
              v-else
              title="Dodaj do ulubionych"
            />
          </button>

          <div class="flat__header">
            <div class="flat__number">
              <p>{{ $t('singleFlat.num') }}</p>
              <h2 class="flat__id">{{ flat[flatProperties['id']] }}</h2>
            </div>
            <div class="flat__status">
              <p>
                {{ $t('singleFlat.status') }}:
                <span :class="status">{{ statusName }}</span>
              </p>
            </div>
          </div>

          <div class="flat__data">
            <div class="flat__leftData">
              <div class="flat__icon">
                <img src="@/assets/flat-area.png" alt="area" />
              </div>
              <p class="flat__label">{{ $t('singleFlat.area') }}:</p>
            </div>
            <p class="flat__value">
              {{ parseInt(flat[flatProperties['area']])
              }}<span class="flat__unit">m<sup>2</sup> </span>
            </p>
          </div>

          <div class="flat__data">
            <div class="flat__leftData">
              <div class="flat__icon">
                <img src="@/assets/flat-floor.png" alt="floor" />
              </div>
              <p class="flat__label">{{ $t('singleFlat.floor') }}:</p>
            </div>
            <p class="flat__value">
              {{ flat[flatProperties['floor']] }}
            </p>
          </div>

          <div class="flat__data">
            <div class="flat__leftData">
              <div class="flat__icon">
                <img src="@/assets/flat-room.png" alt="room" />
              </div>
              <p class="flat__label">{{ $t('singleFlat.rooms') }}:</p>
            </div>
            <p class="flat__value">
              {{ flat[flatProperties['rooms']] }}
            </p>
          </div>

          <div class="flat__buttons">
            <button class="flat__button" @click="toggleForm">
              <span v-if="formActive">{{ $t('singleFlat.closeForm') }}</span>
              <span v-else>{{ $t('singleFlat.askPrice') }}</span>
            </button>
            <a
              v-if="pdfURL"
              :href="pdfURL"
              class="flat__button--outline"
              target="_blank"
              @click="sendPDFEvent"
            >
              <img
                class="flat__buttonIcon"
                src="@/assets/download.png"
                alt="download"
              />
              {{ $t('singleFlat.downloadPDF') }}
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapMutations, mapState, mapGetters } from 'vuex';
import { ValidateFlat } from '@/plugins/routeValidator';

// Components
import zoomImage from '@/components/zoom-image.vue';
import flatForm from '@/components/flat/flat-form.vue';
import card from '@/components/flat/flat-preview-card.vue';

export default {
  name: 'Flat',
  components: { card, flatForm, zoomImage },
  data() {
    return {
      currentView: '2D',
      formActive: false,
      planError: false,
      threeDError: true,
    };
  },
  methods: {
    ...mapMutations({
      addToFavourites: 'ADD_TO_FAVOURITES',
      deleteFromFavourites: 'DELETE_FROM_FAVOURITES',
    }),
    toggleFavourite() {
      if (this.isFavourite) {
        this.deleteFromFavourites(this.flat);
        window.top.postMessage(
          `REMOVE_FAVOURITE/${this.flat[this.flatProperties['id']]}`,
          '*'
        );
      } else {
        this.addToFavourites(this.flat);
        window.top.postMessage(
          `ADD_FAVOURITE/${this.flat[this.flatProperties['id']]}`,
          '*'
        );
      }
    },
    toggleForm() {
      this.formActive = !this.formActive;
      if (this.formActive) {
        window.top.postMessage(
          `ZAPYTAJ_O_CENE/${this.flat[this.flatProperties['id']]}`,
          '*'
        );
      }
    },
    changeView(view) {
      this.currentView = view;
    },
    validateFlat() {
      if (
        !ValidateFlat(this.$route.params, this.flat, this.flats) ||
        this.statusName === this.soldStatusName
      ) {
        this.$router.replace('/404');
      }
    },
    sendPDFEvent() {
      window.top.postMessage(
        `PDF/${this.flat[this.flatProperties['realID']]}`,
        '*'
      );
    },
  },
  computed: {
    ...mapState({
      flatProperties: state => state.browser.flatProperties,
      flats: state => state.browser.flats,
      sidebarActive: state => state.browser.sidebarActive,
    }),
    ...mapGetters({
      getPDF: 'GET_PDF',
      getPlan: 'GET_PLAN',
      get3D: 'GET_3D',
      getStatusClass: 'GET_STATUS_CLASS',
      getStatusName: 'GET_STATUS_NAME',
      flat: 'GET_CURRENT_FLAT',
      checkIfFavourite: 'IS_FAVOURITE',
    }),

    similars() {
      return this.$store.getters.GET_SIMILAR_FLAT(this.flat, 3);
    },
    isFavourite() {
      return this.checkIfFavourite(this.flat);
    },
    status() {
      return this.getStatusClass(this.flat[this.flatProperties['status']]);
    },
    statusName() {
      return this.getStatusName(this.flat[this.flatProperties['status']]);
    },
    soldStatusName() {
      return this.getStatusName(this.flatProperties['status_sold']);
    },
    pdfURL() {
      if (!this.flat) return '';
      return this.getPDF(this.flat[this.flatProperties['realID']]);
    },
    planURL() {
      if (this.planError) {
        return false;
      }
      if (!this.flat) return false;
      return this.getPlan(this.flat[this.flatProperties['realID']]);
    },
    threeURL() {
      if (this.threeDError) {
        return false;
      }
      if (!this.flat) return false;
      return this.get3D(this.flat[this.flatProperties['realID']]);
    },
  },
  watch: {
    flats() {
      this.validateFlat();
    },
  },
  beforeRouteEnter(to, from, next) {
    if (ValidateFlat(to.params)) {
      next();
    } else {
      next('/404');
    }
  },
  beforeRouteUpdate(to, from, next) {
    if (ValidateFlat(to.params)) {
      next();
    } else {
      next('/404');
    }
  },
  mounted() {
    this.validateFlat();
    window.top.postMessage(
      `KARTA_MIESZKAN/${this.flat[this.flatProperties['realID']]}`,
      '*'
    );
  },
};
</script>

<style lang="scss">
sup {
  font-size: 10px;
  vertical-align: super;
}

.flat__wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
  height: 100%;

  transition: 250ms;

  padding: {
    left: 0.7rem;
    right: 0.7rem;
  }

  @media screen and (max-width: $sm) {
    height: auto;
  }

  @media screen and (min-width: $lg + 1px) {
    &.active {
      padding-right: 350px;
    }
  }
}

.flat__container {
  max-width: 1200px;
  width: 100%;
  height: 100%;
  margin: {
    left: auto;
    right: auto;
  }
  padding: {
    left: 10px;
    right: 10px;
    top: 60px;
  }

  @media screen and (max-width: $sm) {
    height: auto;
    padding: {
      top: 80px;
      bottom: 10px;
    }
  }
}

.flat {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  @media screen and (max-width: $sm) {
    flex-direction: column;
    height: auto;
  }
}

.flat__heading {
  font-weight: 500;
  font-size: 14px;
  padding-left: 10px;
  margin: {
    top: 10px;
    bottom: 4px;
  }
}

.flat__cards {
  width: 100%;
  display: flex;
  justify-content: center;
}

@keyframes zoom {
  0% {
    opacity: 0;
    transform: scale(0.4);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

.flat__imageWrapper {
  width: 60%;
  min-width: 400px;
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  @media screen and (max-width: $sm) {
    max-width: 100%;
    width: 100%;
    height: auto;
    margin-bottom: 40px;
  }
}

.flat__image {
  width: 100%;
  height: 70%;
  max-height: 450px;

  position: relative;

  display: flex;
  align-items: center;
  justify-content: center;

  animation: zoom 500ms;

  @media screen and (max-width: $sm) {
    height: 320px;
  }

  img {
    display: block;
    max-width: 100%;
    max-height: 100%;
    //height: 100%;
    //object-fit: contain;
  }
}

.flat__viewButtons {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 6px;
}

.flat__viewButton {
  border: 1px solid $lightGray;
  border-radius: 6px;
  background: none;
  width: 44px;
  height: 44px;
  cursor: pointer;
  font-weight: 400;
  font-size: 14px;
  margin: 0 10px;
  transition: 200ms;
  outline: none;

  &:hover {
    border-color: $primaryColor;
  }

  &.active,
  &:focus {
    background: $primaryColor;
    border-color: $primaryColor;
  }
}

.flat__noimage {
  width: 40vw;
  text-align: center;
  font-size: 22px;
}

.flat__info {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  min-width: 380px;
  max-width: 100vw;
  background: #ffffff;
  box-shadow: 2px 2px 45px rgba(#000, 0.1);
  padding: 2rem;
  padding-top: 2.3rem;
}

.flat__favourite {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translate(-50%, -50%);

  display: flex;
  align-items: center;
  justify-content: center;

  width: 40px;
  height: 40px;

  background: #ffffff;
  box-shadow: 0 0 40px rgba(#000, 0.2);
  border: none;
  border-radius: 50%;

  cursor: pointer;

  img {
    max-height: 50%;
    max-width: 50%;
  }

  &:focus {
    outline: none;
    border: 2px solid $primaryColor;
  }
}

.flat__header {
  display: flex;
  align-items: flex-end;
  width: 100%;
  justify-content: space-between;
  padding-bottom: 1rem;

  border-bottom: 1px solid $lighterGray;
}

.flat__data {
  display: flex;
  justify-content: space-between;
  width: 100%;

  padding: {
    top: 1.2rem;
    bottom: 1.2rem;
  }

  border-bottom: 1px solid $lighterGray;
}

.flat__leftData {
  display: flex;
  align-items: center;
}

.flat__icon {
  margin-right: 16px;
  height: 26px;
  width: 26px;
  img {
    display: block;
    height: 100%;
    width: auto;
    object-fit: contain;
  }
}

.flat__label {
  color: $gray;
  font-size: 14px;
}

.flat__value {
  @extend .flat__id;
  .flat__unit {
    font-size: 1rem;
    letter-spacing: 1px;
  }
}

.flat__number {
  color: $gray;
  font-size: 12px;
  padding-left: 4px;
}

.flat__id {
  margin: 0;
  font-size: 2rem;
  color: #000;
  font-weight: 400;
  line-height: 1.6rem;
  margin-left: -4px;
}

.flat__status {
  display: flex;
  align-items: flex-end;
  font-size: 14px;

  span.available {
    color: $available;
  }

  span.reserved {
    color: $reserved;
  }

  span.sold {
    color: $sold;
  }
}

.flat__buttons {
  margin-top: 1rem;
  width: 100%;
}

.flat__button {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  width: 100%;
  height: 50px;

  margin-bottom: 0.8rem;

  border-radius: 30px;
  border: none;
  background: $primaryColor;

  font-size: 14px;
  color: #000;
  text-decoration: none;

  cursor: pointer;
  transition: 250ms;

  &:hover {
    background: darken($primaryColor, 4%);
  }
}

.flat__buttonIcon {
  position: absolute;
  top: 50%;
  left: 10%;
  transform: translateY(-50%);
  height: 40%;
  img {
    height: 100%;
  }
}

.flat__button--outline {
  @extend .flat__button;

  background: none;
  border: 1px solid $lightGray;

  &:hover {
    background: $lightGray;
  }
}
</style>
